import { DEFAULT_PROFILE, DEFAULT_CONFIG, DEFAULT_DRAWDOWN_PHASES } from '../config/planDefaults';
import { migrateState } from './stateMigration';
import { MAX_PLANS, DEFAULT_PLAN_ID, DEFAULT_PLAN_NAME, cleanPlanName } from './planLibrary';

export const PLAN_SCHEMA_VERSION = 2;
export const MAX_BACKUPS = 3;

const NESTED_PROFILE_KEYS = ['spouse1', 'spouse2', 'accounts', 'pension1', 'pension2', 'strategySettings', 'glide', 'mortgage', 'longTermCare', 'annuity', 'home'];
const isObject = (value) => value != null && typeof value === 'object' && !Array.isArray(value);

/**
 * Turns whatever was stored (an older version, a partial save, or garbage)
 * into a complete plan. Missing fields take their defaults, so a field added
 * in a newer version never crashes an older save.
 */
function normalizeProfile(raw) {
  const stored = isObject(raw) ? raw : {};
  const profile = { ...DEFAULT_PROFILE, ...stored };
  NESTED_PROFILE_KEYS.forEach((key) => {
    profile[key] = { ...DEFAULT_PROFILE[key], ...(isObject(stored[key]) ? stored[key] : {}) };
  });
  // Placeholder names from earlier versions; a single filer was "Spouse 1".
  if (profile.spouse1.name === 'Spouse 1') profile.spouse1.name = DEFAULT_PROFILE.spouse1.name;
  if (profile.spouse2.name === 'Spouse 2') profile.spouse2.name = DEFAULT_PROFILE.spouse2.name;
  Object.keys(profile.accounts).forEach((key) => {
    profile.accounts[key] = Number(profile.accounts[key]) || 0;
  });
  return profile;
}

const normalizeConfig = (raw) => ({ ...DEFAULT_CONFIG, ...(isObject(raw) ? raw : {}) });

// The other plans kept alongside the open one. Anything malformed is dropped
// so one bad entry can never take the rest of the document with it.
function normalizeSavedPlans(raw, openId) {
  const seen = new Set([openId]);
  const plans = [];
  for (const entry of Array.isArray(raw) ? raw : []) {
    if (!isObject(entry) || typeof entry.id !== 'string' || !entry.id || seen.has(entry.id) || !isObject(entry.profile)) continue;
    seen.add(entry.id);
    plans.push({
      id: entry.id,
      name: cleanPlanName(entry.name),
      profile: normalizeProfile(entry.profile),
      simulationConfig: normalizeConfig(entry.simulationConfig),
      savedAt: Number(entry.savedAt) || 0,
    });
    if (plans.length >= MAX_PLANS - 1) break;
  }
  return plans;
}

export function normalizePlan(raw) {
  let source = isObject(raw) ? raw : {};
  if (source.userProfile || source.retirementScenario) source = migrateState(source) || {};

  const planId = typeof source.planId === 'string' && source.planId ? source.planId : DEFAULT_PLAN_ID;
  const results = source.simulationResults;
  return {
    planId,
    planName: cleanPlanName(source.planName, DEFAULT_PLAN_NAME),
    savedPlans: normalizeSavedPlans(source.savedPlans, planId),
    profile: normalizeProfile(source.profile),
    simulationConfig: normalizeConfig(source.simulationConfig),
    // Only keep results that have the shape the results screen reads.
    simulationResults: isObject(results) && isObject(results.percentiles) && isObject(results.finalValues) ? results : null,
    drawdownPhases: Array.isArray(source.drawdownPhases) ? source.drawdownPhases : [...DEFAULT_DRAWDOWN_PHASES],
    selectedHeatmapAge: Number(source.selectedHeatmapAge) || 62,
    monteCarloScenario: typeof source.monteCarloScenario === 'string' ? source.monteCarloScenario : 'median',
  };
}

const inputsOf = (plan) => {
  const normal = normalizePlan(plan);
  // Every plan in the document counts, not only the open one: two documents
  // that differ in a shelved plan are different documents.
  const shelved = normal.savedPlans.map((plan) => [plan.id, plan.name, plan.profile, plan.simulationConfig]);
  return JSON.stringify([normal.profile, normal.simulationConfig, normal.planName, shelved]);
};

export const samePlan = (a, b) => inputsOf(a) === inputsOf(b);
export const isDefaultPlan = (plan) => samePlan(plan, {});

/**
 * First time an account syncs on this device: choose between its cloud copy
 * and the plan already on the device. A device plan that loses is backed up.
 *
 * `cloud` is { data, stamp } from the server, or null if the account has none.
 */
export function resolveFirstSync({ cloud, devicePlan }) {
  if (!cloud) return { use: 'device', backupDevice: false };
  return {
    use: 'cloud',
    backupDevice: !isDefaultPlan(devicePlan) && !samePlan(devicePlan, cloud.data),
  };
}

/**
 * The account already has a working copy on this device. Decide what to do
 * with it now that the cloud copy has been read.
 *
 * `base` is the server stamp of the cloud version the working copy was last
 * in step with. Comparing stamps, never clocks, means a wrong device clock
 * cannot make an edit look older than it is.
 *
 *   'push'                 cloud unchanged since `base`; upload local edits
 *   'keep'                 nothing to do
 *   'takeCloud'            another device saved since; no local edits to lose
 *   'takeCloudBackupLocal' another device saved since AND there are local
 *                          edits: load the cloud copy, keep the local one as a backup
 */
export function reconcileAccountCopy({ cloud, dirty, base }) {
  if (!cloud) return 'push';
  if (cloud.stamp === base) return dirty ? 'push' : 'keep';
  return dirty ? 'takeCloudBackupLocal' : 'takeCloud';
}

/** Backups as stored, tolerating the single-object form an earlier version wrote. */
export function readBackups(stored) {
  const list = Array.isArray(stored) ? stored : (isObject(stored) && stored.plan ? [stored] : []);
  return list.filter((entry) => isObject(entry) && isObject(entry.plan)).slice(0, MAX_BACKUPS);
}

/** Adds a backup to the front of the list, dropping the oldest beyond the limit. */
export function addBackup(list, plan, now = Date.now()) {
  const current = readBackups(list);
  if (current.some((entry) => samePlan(entry.plan, plan))) return current;
  return [{ plan, savedAt: now }, ...current].slice(0, MAX_BACKUPS);
}
